import { getNetwork, getSpotId } from 'app/scripts/services/app-state';
import * as Sentry from '@sentry/react';
import config from 'config/config';
import { Integrations } from '@sentry/tracing';
import { CurrentUser } from '../../current-user/currentUserTypes';
import { getState } from 'state/store';

export interface NotifyErrorOptions {
  level?: Sentry.Severity;
  tags?: Partial<IErrorTags>;
  log?: boolean;

  [extra: string]: any;
}

interface IErrorTags {
  spot_id: string;
  network_id: string;
  main_tab: string;
  secondary_tab: string;
}

const defaultNotifyErrorOptions: NotifyErrorOptions = {
  level: Sentry.Severity.Error
};

class ErrorNotifier {
  static Initialize(): void {
    if (process.env.NODE_ENV !== 'development') {
      Sentry.init({
        dsn: config.sentry.dsn,
        environment: process.env.NODE_ENV,
        integrations: [new Integrations.BrowserTracing()],
        autoSessionTracking: true,
        attachStacktrace: true,
        tracesSampleRate: 1.0,
        beforeSend,
        // will be replaced by webpack
        // @ts-ignore
        release: HOST_PANEL_VERSION
      });
    }
  }

  static setUser(currentUser: Partial<CurrentUser> | null): void {
    if (currentUser === null) {
      Sentry.setUser(null);
      return;
    }

    const { id, email, username, displayName, isSuperAdmin } = currentUser;

    Sentry.setUser({
      id,
      email,
      username,
      display_name: displayName,
      is_super_admin: isSuperAdmin
    });
  }

  /**
   * Raising an error on sentry
   * If you are sending the error manually, please provide string message (instead of the actual error)
   * that describes your error properly, so we can track our in a readable way.
   */
  static notifyError(
    err: Error | string,
    {
      level = Sentry.Severity.Error,
      tags = {},
      log = false,
      // Any additional information you want to be sent to sentry
      ...props
    }: NotifyErrorOptions = defaultNotifyErrorOptions
  ): void {
    if (typeof err === 'string') {
      Sentry.captureMessage(err, { level, tags, extra: props });
    } else {
      Sentry.captureException(err, { level, tags, extra: props });
    }

    if (log) {
      console.error('[ERROR]:', err);
    }
  }
}

const beforeSend = (event: Sentry.Event, hint?: Sentry.EventHint): Sentry.Event => {
  // TODO: When merged to 2.0 use the context wrapper instead
  if (!context) {
    console.error("Couldn't find context on error notifier");
    return event;
  }

  const spotId = getSpotId();
  const route = getState().application.currentRoute;
  const spot = getState()?.spots.map[spotId];
  const tabView = {
    first: route?.tabView?.first || 'external_tab',
    secondary: route?.tabView?.secondary || 'external_tab'
  };

  event.tags = {
    ...event.tags,
    spot_id: spotId,
    network_id: getNetwork().id,
    main_tab: tabView.first,
    secondary_tab: tabView.secondary
  };

  event.contexts = {
    ...event.contexts,
    route: {
      page: route?.page,
      path: route?.path,
      main_tab: tabView.first,
      secondary_tab: tabView.secondary
    },
    spot: {
      ...(spot
        ? {
            id: spot.id,
            name: spot.name,
            url: spot.websiteUrl,
            sso: spot.ssoEnabled
          }
        : {})
    }
  };

  return event;
};

export default ErrorNotifier;
export { Severity } from '@sentry/react';
